import { z } from 'zod';
import { createHiveClient, type HiveClient } from './client.js';
import type { Member } from './members.js';

// Claude's own Hive login, used by the MCP server and the importers. Claude signs
// in with a real auth user (created by a human), so row-level security applies,
// and no secret key sits on a laptop. Everything it writes is labelled Claude,
// working for the founder named in HIVE_OPERATOR_EMAIL.

/** The request header the database reads `on_behalf_of` from (see the Grid migration). */
export const ON_BEHALF_OF_HEADER = 'x-hive-on-behalf-of';

export const claudeEnvSchema = z.object({
  VITE_SUPABASE_URL: z.url({ protocol: /^https?$/ }),
  VITE_SUPABASE_PUBLISHABLE_KEY: z.string().trim().min(1),
  HIVE_CLAUDE_EMAIL: z.email(),
  HIVE_CLAUDE_PASSWORD: z.string().min(1),
  HIVE_OPERATOR_EMAIL: z.email(),
});

export type ClaudeEnv = z.infer<typeof claudeEnvSchema>;

/** Checks the env, naming every missing or bad variable. */
export function parseClaudeEnv(source: Record<string, unknown>): ClaudeEnv {
  const result = claudeEnvSchema.safeParse(source);
  if (result.success) return result.data;
  const problems = result.error.issues.map((issue) => {
    const key = issue.path.join('.');
    return source[key] == null || source[key] === '' ? `${key} is not set` : `${key} is not valid`;
  });
  throw new Error(`Set these in .env (see .env.example): ${[...new Set(problems)].join('; ')}.`);
}

export interface ClaudeSession {
  client: HiveClient;
  /** Claude's members row. */
  claude: Member;
  /** The founder Claude is working for. */
  operator: Member;
}

/**
 * Signs in as Claude and checks the login really is Claude's member and the
 * operator is a human member. Every request after that carries the operator's
 * member id, so activity shows "Claude (for <operator>)".
 */
export async function connectAsClaude(env: ClaudeEnv, baseFetch: typeof fetch = fetch): Promise<ClaudeSession> {
  let onBehalfOf: string | null = null;
  const client = createHiveClient({
    url: env.VITE_SUPABASE_URL,
    publishableKey: env.VITE_SUPABASE_PUBLISHABLE_KEY,
    detectSessionInUrl: false,
    fetch: (input, init) => {
      if (!onBehalfOf) return baseFetch(input, init);
      const headers = new Headers(init?.headers);
      headers.set(ON_BEHALF_OF_HEADER, onBehalfOf);
      return baseFetch(input, { ...init, headers });
    },
  });

  const { data: auth, error: authError } = await client.auth.signInWithPassword({
    email: env.HIVE_CLAUDE_EMAIL,
    password: env.HIVE_CLAUDE_PASSWORD,
  });
  if (authError || !auth.user) {
    throw new Error(`Claude couldn't sign in to Hive as ${env.HIVE_CLAUDE_EMAIL}: ${authError?.message ?? 'no user'}.`);
  }

  const { data: claude, error: claudeError } = await client
    .from('members')
    .select('*')
    .eq('user_id', auth.user.id)
    .maybeSingle();
  if (claudeError) throw new Error(`Couldn't read Claude's member row: ${claudeError.message}`);
  if (!claude) {
    throw new Error(
      `${env.HIVE_CLAUDE_EMAIL} signed in but isn't a studio member. Add it to studio_allowlist with member_kind 'claude'.`,
    );
  }
  if (claude.kind !== 'claude') {
    throw new Error(
      `${env.HIVE_CLAUDE_EMAIL} is a human member. Claude must have its own login whose members row has kind 'claude'.`,
    );
  }

  const operatorEmail = env.HIVE_OPERATOR_EMAIL.toLowerCase();
  const { data: operator, error: operatorError } = await client
    .from('members')
    .select('*')
    .eq('email', operatorEmail)
    .maybeSingle();
  if (operatorError) throw new Error(`Couldn't look up ${operatorEmail}: ${operatorError.message}`);
  if (!operator || operator.kind !== 'human') {
    throw new Error(
      `HIVE_OPERATOR_EMAIL (${operatorEmail}) isn't a human studio member yet. Sign in to Hive with that Google account first.`,
    );
  }

  onBehalfOf = operator.id;
  return { client, claude, operator };
}
